import { useRef, useState } from "react";
import { m as motion, useMotionValue, useReducedMotion } from "framer-motion";

interface Props {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
}

/** Drag-handle to reveal "after" over "before". */
export function BeforeAfterReveal({
  before,
  after,
  beforeLabel = "Råmaterial",
  afterLabel = "Färdig leverans",
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(55);
  const reduce = useReducedMotion();

  const onMove = (clientX: number) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    setPos(p);
  };

  return (
    <div
      ref={ref}
      onMouseMove={(e) => onMove(e.clientX)}
      onTouchMove={(e) => onMove(e.touches[0]?.clientX ?? 0)}
      className="relative aspect-video w-full select-none overflow-hidden rounded-3xl border border-white/10 bg-surface"
    >
      {/* Before (full) */}
      <img src={before} alt={beforeLabel} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <div className="absolute left-4 top-4 z-20 rounded-full bg-black/70 px-3 py-1 text-[11px] uppercase tracking-widest text-white backdrop-blur">
        {beforeLabel}
      </div>

      {/* After (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
      >
        <img src={after} alt={afterLabel} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute right-4 top-4 z-20 rounded-full bg-mint px-3 py-1 text-[11px] uppercase tracking-widest text-mint-foreground">
          {afterLabel}
        </div>
      </div>

      {/* Handle */}
      <motion.div
        className="absolute inset-y-0 z-30 flex w-px items-center justify-center bg-mint shadow-[0_0_20px_4px_color-mix(in_oklab,var(--mint)_45%,transparent)]"
        style={{ left: `${pos}%` }}
        animate={reduce ? undefined : { opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-mint bg-background text-mint">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 6l-6 6 6 6" />
            <path d="M15 6l6 6-6 6" />
          </svg>
        </div>
      </motion.div>
    </div>
  );
}
